// PLAN-permalink-cockpit-convergence Phase D: /v/:id permalink を Cockpit と同じ
// Detail コンポーネントで描画する read-only ビュー。
//
// Facade SSR の手書き HTML を置き換える。fetchPermalinkJson で取得した生 R2 JSON を
// Detail props に normalize し、related.titleMap を resolvedRefs として渡すことで
// 未認証 public 閲覧でも RefList が title 解決済みで表示できる。
import { useEffect, useState } from 'react'
import { Detail } from './common/Detail'
import { EmptyState } from './common/EmptyState'
import { PermalinkButton } from './PermalinkButton'
import { useV2Router } from '../router'
import { fetchPermalinkJson, PermalinkNotFoundError } from '../../api/permalink'
import type { PermalinkRawEntry, PermalinkRelated } from '../../api/permalink'

interface Props {
  id: string
}

export function PermalinkView({ id }: Props) {
  const { navigatePermalink } = useV2Router()
  const [entry, setEntry] = useState<PermalinkRawEntry | null>(null)
  const [related, setRelated] = useState<PermalinkRelated | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setNotFound(false)
    setError(null)
    fetchPermalinkJson(id)
      .then((res) => {
        if (cancelled) return
        // resolved_id !== requested_id で再 fetch 済み。entry が無ければ not-found 扱い
        if (!res.entry) {
          setNotFound(true)
        } else {
          setEntry(res.entry)
          setRelated(res.related ?? null)
        }
        setLoading(false)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        if (e instanceof PermalinkNotFoundError) setNotFound(true)
        else setError(e instanceof Error ? e.message : 'failed to load')
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [id])

  return (
    <div className="h-dvh w-screen flex flex-col bg-bg-base text-text-base overflow-hidden">
      {/* permalink 専用の薄い topbar */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b border-border-subtle text-sm">
        <a href="/v2/" className="text-accent-primary hover:underline">KDB Cockpit</a>
        {entry && (
          <a
            href={`/v2/`}
            onClick={(e) => { e.preventDefault(); navigatePermalink(entry.id) }}
            className="text-text-subtle hover:text-text-base"
            title="この item を Cockpit のレンズで開く"
          >
            Cockpit で開く →
          </a>
        )}
      </div>

      <div className="flex-1 overflow-hidden min-h-0">
        {loading ? (
          <div className="p-6 text-sm text-text-subtle">Loading…</div>
        ) : notFound ? (
          <EmptyState
            title="見つかりませんでした"
            description="この item は存在しないか、閲覧にログインが必要です。"
            action={<a href="/v2/" className="text-accent-primary hover:underline">KDB Cockpit にログイン</a>}
          />
        ) : error ? (
          <EmptyState title="読み込みに失敗しました" description={error} />
        ) : entry ? (
          <PermalinkDetail entry={entry} related={related} onRefOpen={navigatePermalink} />
        ) : null}
      </div>
    </div>
  )
}

function PermalinkDetail({
  entry,
  related,
  onRefOpen,
}: {
  entry: PermalinkRawEntry
  related: PermalinkRelated | null
  onRefOpen: (id: string) => void
}) {
  const meta = entry.article_meta
  const domains = related?.domains ?? []

  return (
    <Detail
      title={entry.title}
      body={entry.content}
      one_line={entry.abstract?.one_line ?? null}
      key_points={entry.abstract?.key_points ?? []}
      origin={entry.origin}
      subjectivity={entry.subjectivity}
      visibility={entry.visibility}
      lifecycle_stage={meta?.lifecycle_stage ?? null}
      source={entry.source ?? null}
      source_actor={entry.source_actor ?? null}
      source_context={entry.source_context ?? null}
      declared_domains={entry.declared_domains ?? []}
      curated={domains.filter((d) => d.provenance === 'curated').map((d) => ({ domain: d.domain, provenance: 'curated' as const, confidence: d.confidence }))}
      inferred={domains.filter((d) => d.provenance === 'inferred').map((d) => ({ domain: d.domain, provenance: 'inferred' as const, confidence: d.confidence }))}
      created_at={entry.created_at}
      article_meta={meta ? {
        theme: meta.theme,
        version_label: meta.version_label,
        confidence: meta.confidence,
        applicability: meta.applicability,
        confidence_basis: meta.confidence_basis,
      } : undefined}
      source_entry_ids={meta?.source_entry_ids ?? []}
      source_article_ids={entry.source_article_ids ?? []}
      previous_version={entry.previous_version ?? null}
      decision={entry.decision ?? null}
      has_decision={entry.decision ? 1 : 0}
      x_status={entry.decision?.x_status ?? null}
      resolvedRefs={related?.titleMap}
      onRefOpen={(refId) => onRefOpen(refId)}
      headerActions={<PermalinkButton id={entry.id} visibility={entry.visibility ?? null} showId={true} />}
    />
  )
}
